<template>
    <b-modal :visible="value" @change="onVisibleChange" title="Write Off Balance" hide-footer>
        <b-alert :show="reasonCodesUnavailable" variant="warning" class="py-2">
            A write-off requires a reason code, but no Statement Reason / Credit Codes are
            configured. An administrator must add them under Configuration &gt; AR / Patient Billing.
        </b-alert>

        <b-row>
            <b-col cols="6">
                <b-form-group label="Amount">
                    <b-form-input v-model.number="form.amount" type="number" min="0" step="0.01" />
                </b-form-group>
            </b-col>
            <b-col cols="6">
                <b-form-group :label="reasonCodeLabel">
                    <b-select v-if="useReasonCodeList" v-model="form.reasonCode" :options="reasonCodeOptions"
                        :disabled="reasonCodesUnavailable" />
                    <b-form-input v-else v-model="form.reasonCode" />
                </b-form-group>
            </b-col>
        </b-row>
        <b-form-group label="Notes">
            <b-form-input v-model="form.notes" />
        </b-form-group>
        <small class="text-muted">
            Credits the amount against this account's balance. To write off the account itself and
            close it, use <strong>Write Off Account</strong> in the Manage window.
        </small>

        <div class="text-right mt-2">
            <b-button variant="secondary" class="mr-2" :disabled="saving" @click="close">Cancel</b-button>
            <b-button variant="danger"
                :disabled="saving || reasonCodesUnavailable || !(form.amount > 0) || !form.reasonCode"
                @click="postWriteOff">
                Write Off Balance
            </b-button>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import ARReasonCodesMixin from "@/mixins/ARReasonCodesMixin";

@Component({
    name: "ARWriteOffModal",
})
export default class ARWriteOffModal extends Mixins(ARReasonCodesMixin) {
    @Prop({ type: Boolean, default: false }) value!: boolean;
    @Prop({ default: null }) accountId!: number | null;

    private saving = false;
    private form = this.emptyForm();

    @Watch("value")
    async onShow(visible: boolean): Promise<void> {
        if (!visible) return;
        this.form = this.emptyForm();
        await this.loadReasonCodeConfiguration();
        this.form.reasonCode = this.knownReasonCode(this.form.reasonCode);
    }

    private emptyForm() {
        return { amount: 0, reasonCode: "", notes: "" };
    }

    // A write-off reduces the balance owed, so it always posts as a credit regardless of how the
    // amount was typed.
    buildPayload() {
        return {
            amount: -Math.abs(this.form.amount),
            reasonCode: this.form.reasonCode,
            notes: this.form.notes,
        };
    }

    async postWriteOff(): Promise<void> {
        if (!this.accountId) return;
        try {
            this.saving = true;
            await axios.post(`/AR/accounts/${this.accountId}/write-offs`, this.buildPayload());
            this.$notification(NotificationOptions.success("Write-off posted."));
            this.$emit("saved");
            this.close();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    onVisibleChange(visible: boolean): void {
        this.$emit("input", visible);
    }

    close(): void {
        this.$emit("input", false);
    }
}
</script>

<style scoped></style>
